import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, ChevronLeft, Check, Circle, Loader2,
  CheckCircle2, ArrowRight, Target, AlertTriangle, ChevronDown, ChevronUp
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const KOSHA_COLORS = {
  annamaya: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", bar: "bg-red-500", accent: "bg-red-500/20" },
  pranamaya: { bg: "bg-orange-500/10", border: "border-orange-500/30", text: "text-orange-400", bar: "bg-orange-500", accent: "bg-orange-500/20" },
  manomaya: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", bar: "bg-amber-500", accent: "bg-amber-500/20" },
  vijnanamaya: { bg: "bg-sky-500/10", border: "border-sky-500/30", text: "text-sky-400", bar: "bg-sky-500", accent: "bg-sky-500/20" },
  anandamaya: { bg: "bg-violet-500/10", border: "border-violet-500/30", text: "text-violet-400", bar: "bg-violet-500", accent: "bg-violet-500/20" },
};

const STATUS_STYLES = {
  Blocked: { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-400", bar: "bg-red-500" },
  Partial: { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-400", bar: "bg-amber-500" },
  Open: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-400", bar: "bg-emerald-500" },
};

export default function KoshaQuiz({ token, userId, quizType = "life", mode = "full", onRetake: onRetakeCallback, onComplete: onCompleteCallback, completedItems, onToggle, sectionId }) {
  const containerRef = useRef(null);
  const [koshas, setKoshas] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [activeKoshaIdx, setActiveKoshaIdx] = useState(0);
  const [scores, setScores] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quizView, setQuizView] = useState("card"); // "card" or "quiz"
  const [isCollapsed, setIsCollapsed] = useState(true);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const loadQuestions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/kosha-quiz/questions/${quizType}`, { headers });
      const data = await res.json();
      setQuestions(data.questions || []);
      setKoshas(data.koshas || []);
    } catch (e) { console.error("Failed to load kosha quiz questions", e); }
  }, [quizType]);

  const loadResults = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/kosha-quiz/results/${quizType}/${userId}`, { headers });
      const data = await res.json();
      if (data.completed) {
        setCompleted(true);
        setScores(data.scores);
        setAnswers(data.answers || {});
      } else if (data.answers && Object.keys(data.answers).length > 0) {
        setAnswers(data.answers);
      }
    } catch (e) { console.error("Failed to load kosha quiz results", e); }
  }, [quizType, userId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadQuestions();
      await loadResults();
      setLoading(false);
    })();
  }, [quizType, userId]);

  const currentKosha = koshas[activeKoshaIdx];
  const currentQuestions = currentKosha
    ? questions.filter(q => q.kosha === currentKosha.id)
    : [];
  const currentAnswered = currentQuestions.filter(q => answers[String(q.qid)] !== undefined).length;
  const totalAnswered = Object.keys(answers).length;
  const koshaColors = currentKosha ? KOSHA_COLORS[currentKosha.id] || KOSHA_COLORS.annamaya : KOSHA_COLORS.annamaya;

  const handleAnswer = (qid, value) => {
    setAnswers(prev => ({ ...prev, [String(qid)]: value }));
  };

  const goToKosha = (idx) => {
    setActiveKoshaIdx(idx);
    setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/kosha-quiz/save`, {
        method: "POST", headers,
        body: JSON.stringify({ quiz_type: quizType, answers }),
      });
      if (!res.ok) {
        const err = await res.text();
        console.error("Kosha save failed:", res.status, err);
        toast.error(`Save failed (${res.status}). Please retry or contact support.`);
        setSaving(false);
        return;
      }
      const data = await res.json();
      if (data.scores) {
        setScores(data.scores);
        setCompleted(true);
        setQuizView("card");
        toast.success("Kosha Quiz completed! View your results.");
        // Notify parent so results card re-fetches immediately
        if (onCompleteCallback) onCompleteCallback(quizType);
      } else {
        toast.error("Save succeeded but no scores returned. Please contact support.");
      }
    } catch (e) {
      console.error("Save failed", e);
      toast.error("Network error. Please retry.");
    }
    setSaving(false);
  };

  const handleRetake = async () => {
    try {
      await fetch(`${API}/api/kosha-quiz/retake`, {
        method: "POST", headers,
        body: JSON.stringify({ quiz_type: quizType }),
      });
    } catch (e) { console.error("Failed to reset kosha quiz on server", e); }
    setCompleted(false);
    setScores(null);
    setAnswers({});
    setActiveKoshaIdx(0);
    setQuizView("quiz");
    if (onRetakeCallback) onRetakeCallback(quizType);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-divine-gold animate-spin" />
        <span className="ml-3 text-chakra-crown/50 text-sm">Loading Kosha Quiz...</span>
      </div>
    );
  }

  // ====== TEST MODE: Card overview → Quiz drill-down ======
  if (mode === "test") {
    // Quiz drill-down view
    if (quizView === "quiz") {
      return (
        <div className="space-y-5" data-testid={`kosha-quiz-${quizType}`} ref={containerRef}>
          <button
            onClick={() => { setQuizView("card"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
            className="flex items-center gap-2 text-sm text-chakra-crown/50 hover:text-divine-gold transition-colors"
            data-testid="exit-kosha-quiz-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Overview
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg text-divine-gold">DEEP DIVE KOSHA QUIZ</h2>
              <p className="text-chakra-crown/50 text-sm mt-1">
                {quizType === "relationship" ? "Relationship" : "Life"} - 25 questions across 5 Koshas
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs text-chakra-crown/40">Progress</span>
              <p className="text-divine-gold font-heading text-sm">{totalAnswered}/25</p>
            </div>
          </div>

          <Progress value={(totalAnswered / 25) * 100} className="h-2" />

          {/* Kosha Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="kosha-tabs">
            {koshas.map((k, idx) => {
              const kQuestions = questions.filter(q => q.kosha === k.id);
              const kAnswered = kQuestions.filter(q => answers[String(q.qid)] !== undefined).length;
              const isComplete = kAnswered === kQuestions.length;
              const kc = KOSHA_COLORS[k.id] || KOSHA_COLORS.annamaya;
              return (
                <button key={k.id} onClick={() => goToKosha(idx)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap text-sm border ${
                    activeKoshaIdx === idx
                      ? `${kc.bg} ${kc.border} ${kc.text}`
                      : isComplete
                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                        : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/50 hover:border-divine-gold/20"
                  }`}
                  data-testid={`kosha-tab-${k.id}`}
                >
                  {isComplete && activeKoshaIdx !== idx ? <Check className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                  <span className="font-medium">{k.name.replace(" Kosha", "")}</span>
                  <span className="text-xs opacity-60">{kAnswered}/{kQuestions.length}</span>
                </button>
              );
            })}
          </div>

          {/* Current Kosha Questions */}
          <AnimatePresence mode="wait">
            {currentKosha && (
              <motion.div key={currentKosha.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className={`p-4 rounded-xl border ${koshaColors.border} ${koshaColors.bg}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${koshaColors.accent} flex items-center justify-center`}>
                      <span className={`font-heading text-lg ${koshaColors.text}`}>{activeKoshaIdx + 1}</span>
                    </div>
                    <div>
                      <h3 className={`font-heading text-base ${koshaColors.text}`}>{currentKosha.name}</h3>
                      <p className="text-chakra-crown/50 text-xs">{currentKosha.english}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <span className="text-xs text-chakra-crown/40">{currentAnswered}/{currentQuestions.length} answered</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {currentQuestions.map((q, idx) => {
                    const selected = answers[String(q.qid)];
                    return (
                      <div key={q.qid} className="sacred-card" data-testid={`kosha-q-${q.qid}`}>
                        <div className="flex items-start gap-3 mb-3">
                          <span className="text-divine-gold/40 text-sm font-heading shrink-0 mt-0.5">
                            Q{(activeKoshaIdx * 5) + idx + 1}
                          </span>
                          <p className="text-chakra-crown text-sm leading-relaxed">{q.text}</p>
                        </div>
                        <div className="space-y-2">
                          {q.options.map((opt, oi) => {
                            const isSelected = selected === opt.value;
                            return (
                              <button key={oi} onClick={() => handleAnswer(q.qid, opt.value)}
                                className={`w-full text-left p-3 rounded-lg border transition-all ${
                                  isSelected
                                    ? `${koshaColors.bg} ${koshaColors.border} ${koshaColors.text}`
                                    : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/70 hover:border-divine-gold/30"
                                }`}
                                data-testid={`kosha-q-${q.qid}-opt-${oi}`}
                              >
                                <div className="flex items-center gap-3">
                                  {isSelected
                                    ? <CheckCircle2 className={`w-4 h-4 ${koshaColors.text} shrink-0`} />
                                    : <Circle className="w-4 h-4 text-chakra-crown/30 shrink-0" />}
                                  <span className="text-sm">{opt.label}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-2">
                  <div>
                    {activeKoshaIdx > 0 && (
                      <button onClick={() => goToKosha(activeKoshaIdx - 1)}
                        className="flex items-center gap-2 text-chakra-crown/50 hover:text-divine-gold transition-colors text-sm"
                        data-testid="kosha-prev">
                        <ChevronLeft className="w-4 h-4" /> Previous Kosha
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setQuizView("card"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-divine-gold/20 text-chakra-crown/60 hover:text-divine-gold hover:border-divine-gold/40 text-sm transition-all"
                      data-testid="exit-kosha-quiz-bottom-btn"
                    >
                      Exit
                    </button>
                    {activeKoshaIdx < koshas.length - 1 ? (
                      <button onClick={() => goToKosha(activeKoshaIdx + 1)}
                        disabled={currentAnswered === 0}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                        data-testid="kosha-next">
                        <ArrowRight className="w-4 h-4" />
                        Next Kosha
                      </button>
                    ) : (
                      <button onClick={handleSave}
                        disabled={saving || totalAnswered < 25}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                        data-testid="kosha-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                        SUBMIT QUIZ
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    // Card overview
    return (
      <div className="rounded-xl border border-divine-gold/10 bg-cosmic-deep/40 overflow-hidden" ref={containerRef}>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full flex items-center justify-between px-4 py-3 bg-cosmic-deep/60 border-b border-divine-gold/10"
          data-testid={`kosha-quiz-toggle-${quizType}`}
        >
          <div className="flex items-center gap-2">
            <Target className={`w-3.5 h-3.5 ${completed ? "text-emerald-400" : "text-violet-400"}`} />
            <span className="text-divine-gold text-xs font-heading tracking-wider">DEEP DIVE KOSHA QUIZ</span>
            <span className="text-chakra-crown/40 text-[10px]">{quizType === "relationship" ? "RELATIONSHIP" : "LIFE"}</span>
            {completed && (
              <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-semibold">
                COMPLETE
              </span>
            )}
            {!completed && totalAnswered > 0 && (
              <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20 font-semibold">
                {totalAnswered}/25
              </span>
            )}
          </div>
          {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-divine-gold/50" /> : <ChevronUp className="w-3.5 h-3.5 text-divine-gold/50" />}
        </button>
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="p-4">
                <button
                  onClick={() => { setQuizView("quiz"); setTimeout(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                  className={`w-full text-left p-4 rounded-xl border transition-all hover:scale-[1.01] ${
                    completed
                      ? "bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40"
                      : totalAnswered > 0
                        ? "bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40"
                        : "bg-cosmic-light/5 border-divine-gold/10 hover:border-divine-gold/30"
                  }`}
                  data-testid={`kosha-quiz-card-${quizType}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${completed ? "bg-emerald-500/10" : "bg-violet-500/10"}`}>
                      <Target className={`w-5 h-5 ${completed ? "text-emerald-400" : "text-violet-400"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-heading truncate ${completed ? "text-emerald-400" : "text-violet-400"}`}>5 KOSHAS ASSESSMENT</p>
                        {completed && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                      </div>
                      <p className="text-xs text-chakra-crown/40 font-heading">
                        {quizType === "relationship" ? "Relationship" : "Life"} - 5 Koshas
                      </p>
                      <p className="text-xs text-chakra-crown/50 mt-1.5 leading-relaxed line-clamp-2">
                        {completed
                          ? `Completed | Overall: ${scores?.overall_score?.toFixed(1)}/5.0 (${scores?.overall_status})`
                          : totalAnswered > 0
                            ? `In progress - ${totalAnswered}/25 answered`
                            : "Explore your five layers of being: Annamaya, Pranamaya, Manomaya, Vijnanamaya, Anandamaya"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <ChevronRight className={`w-4 h-4 shrink-0 ${completed ? "text-emerald-400/40" : "text-divine-gold/40"}`} />
                      {completed && (
                        <span
                          onClick={(e) => { e.stopPropagation(); handleRetake(); }}
                          className="text-[10px] px-2 py-0.5 rounded border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors cursor-pointer"
                          data-testid={`retake-kosha-quiz-${quizType}`}
                        >
                          Retake
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ====== RESULTS MODE: Show scores only (no recommendations) ======
  if (mode === "results") {
    if (!completed || !scores) {
      return (
        <div className="rounded-xl border border-divine-gold/10 bg-cosmic-deep/40 overflow-hidden">
          <div className="px-4 py-3 bg-cosmic-deep/60 flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-divine-gold text-xs font-heading tracking-wider">KOSHA ASSESSMENT RESULTS</span>
          </div>
          <div className="text-center py-4 px-4" data-testid={`kosha-quiz-no-results-${quizType}`}>
            <Target className="w-6 h-6 text-divine-gold/20 mx-auto mb-2" />
            <p className="text-chakra-crown/50 text-sm">
              Complete the {quizType === "relationship" ? "Relationship" : "Life"} Kosha Quiz in Diagnostic Tests to see results
            </p>
          </div>
        </div>
      );
    }
    const koshaOrder = ["annamaya", "pranamaya", "manomaya", "vijnanamaya", "anandamaya"];
    return (
      <div className="rounded-xl border border-divine-gold/10 bg-cosmic-deep/40 overflow-hidden" data-testid={`kosha-quiz-results-${quizType}`} ref={containerRef}>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full flex items-center justify-between px-4 py-3 bg-cosmic-deep/60 border-b border-divine-gold/10"
          data-testid={`kosha-results-toggle-${quizType}`}
        >
          <div className="flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-divine-gold text-xs font-heading tracking-wider">KOSHA ASSESSMENT RESULTS</span>
            <span className="text-chakra-crown/40 text-[10px]">{quizType === "relationship" ? "RELATIONSHIP" : "LIFE"}</span>
            <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-semibold">
              {scores.overall_score?.toFixed(1)}/5.0
            </span>
          </div>
          {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-divine-gold/50" /> : <ChevronUp className="w-3.5 h-3.5 text-divine-gold/50" />}
        </button>
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="p-4 space-y-4">
        <div className="p-3 rounded-xl border border-divine-gold/20 bg-divine-gold/5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-chakra-crown/50">Overall Score</span>
            <div className="flex items-center gap-2">
              <span className="font-heading text-base text-divine-gold">{scores.overall_score?.toFixed(1)} / 5.0</span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_STYLES[scores.overall_status]?.bg} ${STATUS_STYLES[scores.overall_status]?.text}`}>{scores.overall_status}</span>
            </div>
          </div>
          <Progress value={scores.overall_percentage || 0} className="h-2 bg-cosmic-deep [&>div]:bg-divine-gold" />
        </div>
        <div className="space-y-2">
          {koshaOrder.map(kid => {
            const k = scores.koshas?.[kid];
            if (!k) return null;
            const sc = STATUS_STYLES[k.status] || STATUS_STYLES.Partial;
            const kc = KOSHA_COLORS[kid] || KOSHA_COLORS.annamaya;
            return (
              <div key={kid} className={`p-3 rounded-lg border ${sc.border} ${sc.bg}`} data-testid={`kosha-score-${kid}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`font-heading text-sm ${kc.text}`}>{k.name} <span className="text-chakra-crown/40 text-xs font-normal">({k.english})</span></span>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-heading ${sc.text}`}>{k.score?.toFixed(1)}/5</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${sc.bg} ${sc.text}`}>{k.status}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-cosmic-deep rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${sc.bar}`} style={{ width: `${k.percentage}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        {scores.priority_koshas?.length > 0 && (
          <div className="p-2.5 rounded-lg border border-amber-500/20 bg-amber-500/5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-amber-400 font-heading text-xs">PRIORITY:</span>
              <span className="text-chakra-crown/60 text-xs">{scores.priority_koshas.map(k => scores.koshas?.[k]?.name || k).join(", ")}</span>
            </div>
          </div>
        )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ====== ALIGNMENT MODE: Show recommendations only (for Alignment tab) ======
  if (mode === "alignment") {
    if (!completed || !scores) {
      return (
        <div className="text-center py-4" data-testid={`kosha-quiz-no-alignment-${quizType}`}>
          <Target className="w-6 h-6 text-divine-gold/20 mx-auto mb-2" />
          <p className="text-chakra-crown/50 text-sm">
            Complete the Kosha Quiz in Awareness → Diagnostic Tests to see alignment recommendations
          </p>
        </div>
      );
    }
    const koshaOrder = ["annamaya", "pranamaya", "manomaya", "vijnanamaya", "anandamaya"];
    return (
      <div className="space-y-3" data-testid={`kosha-alignment-${quizType}`} ref={containerRef}>
        {koshaOrder.map(kid => {
          const k = scores.koshas?.[kid];
          if (!k || !k.recommendations?.length) return null;
          const sc = STATUS_STYLES[k.status] || STATUS_STYLES.Partial;
          const kc = KOSHA_COLORS[kid] || KOSHA_COLORS.annamaya;
          return (
            <div key={kid} className={`p-4 rounded-xl border ${sc.border} ${sc.bg}`} data-testid={`kosha-alignment-${kid}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`font-heading text-sm ${kc.text}`}>{k.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${sc.bg} ${sc.text}`}>{k.status} ({k.score?.toFixed(1)}/5)</span>
              </div>
              <div className="space-y-1.5">
                {k.recommendations.map((rec, i) => {
                  const itemKey = sectionId ? `${sectionId}_${kid}_${i}` : null;
                  const isChecked = itemKey && completedItems?.includes(itemKey);
                  return (
                    <div key={i} className={`flex items-start gap-2 ${itemKey ? 'p-2 rounded-lg transition-all ' + (isChecked ? 'bg-emerald-500/10' : 'hover:bg-cosmic-light/5') : ''}`}>
                      {itemKey && onToggle ? (
                        <button onClick={() => onToggle(itemKey)}
                          className={`w-5 h-5 mt-0.5 rounded flex items-center justify-center transition-colors border-2 shrink-0 ${isChecked ? "bg-emerald-500 border-emerald-500" : "border-divine-gold/40 hover:border-divine-gold"}`}
                          data-testid={`kosha-check-${kid}-${i}`}>
                          {isChecked && <Check className="w-3 h-3 text-white" />}
                        </button>
                      ) : (
                        <ArrowRight className="w-3.5 h-3.5 text-divine-gold mt-0.5 shrink-0" />
                      )}
                      <p className={`text-sm leading-relaxed ${isChecked ? 'text-emerald-300/80' : 'text-chakra-crown/70'}`}>{rec}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {koshaOrder.every(kid => !scores.koshas?.[kid]?.recommendations?.length) && (
          <p className="text-chakra-crown/50 text-center py-4 text-sm">All Koshas are Open — no alignment actions needed</p>
        )}
      </div>
    );
  }

  // ====== RESULTS VIEW ======
  if (completed && scores) {
    return (
      <div className="space-y-5" data-testid={`kosha-quiz-results-${quizType}`} ref={containerRef}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg text-divine-gold">
              DEEP DIVE KOSHA ASSESSMENT
            </h2>
            <p className="text-chakra-crown/50 text-sm mt-1">
              {quizType === "relationship" ? "Relationship" : "Life"} Kosha Analysis
            </p>
          </div>
          {mode !== "results" && (
            <button
              onClick={handleRetake}
              className="text-xs px-3 py-1.5 rounded-lg border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors"
              data-testid="retake-kosha-quiz"
            >
              Retake Quiz
            </button>
          )}
        </div>

        {/* Overall Score */}
        <div className="p-4 rounded-xl border border-divine-gold/20 bg-divine-gold/5" data-testid="kosha-overall-score">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-chakra-crown/60">Overall Kosha Score</span>
            <span className="font-heading text-lg text-divine-gold">
              {scores.overall_score?.toFixed(1)} / 5.0
            </span>
          </div>
          <Progress value={scores.overall_percentage || 0} className="h-2.5 bg-cosmic-deep [&>div]:bg-divine-gold" />
          <div className="flex items-center justify-between mt-2">
            <span className={`text-xs font-medium ${STATUS_STYLES[scores.overall_status]?.text || "text-amber-400"}`}>
              {scores.overall_status}
            </span>
            <span className="text-xs text-chakra-crown/40">{scores.overall_message}</span>
          </div>
        </div>

        {/* Per-Kosha Scores */}
        <div className="space-y-3">
          {koshas.map(k => {
            const data = scores.koshas?.[k.id];
            if (!data) return null;
            const sc = STATUS_STYLES[data.status] || STATUS_STYLES.Partial;
            const kc = KOSHA_COLORS[k.id] || KOSHA_COLORS.annamaya;
            return (
              <div key={k.id} className={`p-4 rounded-xl border ${sc.border} ${sc.bg}`} data-testid={`kosha-score-${k.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className={`font-heading text-sm ${kc.text}`}>{k.name}</span>
                    <span className="text-chakra-crown/40 text-xs ml-2">({k.english})</span>
                  </div>
                  <div className="text-right">
                    <span className={`font-heading text-base ${sc.text}`}>{data.score?.toFixed(1)}</span>
                    <span className="text-xs text-chakra-crown/30">/5</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>{data.status}</span>
                  </div>
                </div>
                <div className="h-2 bg-cosmic-deep rounded-full overflow-hidden mb-2">
                  <div className={`h-full rounded-full ${sc.bar} transition-all duration-700`} style={{ width: `${data.percentage}%` }} />
                </div>
                {data.recommendations?.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {data.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <ArrowRight className="w-3.5 h-3.5 text-divine-gold mt-0.5 shrink-0" />
                        <p className="text-chakra-crown/70 leading-relaxed">{rec}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Priority Koshas */}
        {scores.priority_koshas?.length > 0 && (
          <div className="p-3 rounded-xl border border-amber-500/20 bg-amber-500/5" data-testid="priority-koshas">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-heading text-sm">PRIORITY FOCUS AREAS</span>
            </div>
            <p className="text-chakra-crown/60 text-sm">
              {scores.priority_koshas.map(k => {
                const kosha = koshas.find(ko => ko.id === k);
                return kosha ? kosha.name : k;
              }).join(", ")}
            </p>
          </div>
        )}
      </div>
    );
  }

  // ====== QUIZ VIEW ======
  return (
    <div className="space-y-5" data-testid={`kosha-quiz-${quizType}`} ref={containerRef}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg text-divine-gold">DEEP DIVE KOSHA QUIZ</h2>
          <p className="text-chakra-crown/50 text-sm mt-1">
            {quizType === "relationship" ? "Relationship" : "Life"} - 25 questions across 5 Koshas
          </p>
        </div>
        <div className="text-right">
          <span className="text-xs text-chakra-crown/40">Progress</span>
          <p className="text-divine-gold font-heading text-sm">{totalAnswered}/25</p>
        </div>
      </div>

      <Progress value={(totalAnswered / 25) * 100} className="h-2" />

      {/* Kosha Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="kosha-tabs">
        {koshas.map((k, idx) => {
          const kQuestions = questions.filter(q => q.kosha === k.id);
          const kAnswered = kQuestions.filter(q => answers[String(q.qid)] !== undefined).length;
          const isComplete = kAnswered === kQuestions.length;
          const kc = KOSHA_COLORS[k.id] || KOSHA_COLORS.annamaya;
          return (
            <button key={k.id} onClick={() => goToKosha(idx)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap text-sm border ${
                activeKoshaIdx === idx
                  ? `${kc.bg} ${kc.border} ${kc.text}`
                  : isComplete
                    ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                    : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/50 hover:border-divine-gold/20"
              }`}
              data-testid={`kosha-tab-${k.id}`}
            >
              {isComplete && activeKoshaIdx !== idx ? <Check className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
              <span className="font-medium">{k.name.replace(" Kosha", "")}</span>
              <span className="text-xs opacity-60">{kAnswered}/{kQuestions.length}</span>
            </button>
          );
        })}
      </div>

      {/* Current Kosha Header + Questions */}
      <AnimatePresence mode="wait">
        {currentKosha && (
          <motion.div key={currentKosha.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <div className={`p-4 rounded-xl border ${koshaColors.border} ${koshaColors.bg}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${koshaColors.accent} flex items-center justify-center`}>
                  <span className={`font-heading text-lg ${koshaColors.text}`}>{activeKoshaIdx + 1}</span>
                </div>
                <div>
                  <h3 className={`font-heading text-base ${koshaColors.text}`}>{currentKosha.name}</h3>
                  <p className="text-chakra-crown/50 text-xs">{currentKosha.english}</p>
                </div>
                <div className="ml-auto text-right">
                  <span className="text-xs text-chakra-crown/40">{currentAnswered}/{currentQuestions.length} answered</span>
                </div>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-3">
              {currentQuestions.map((q, idx) => {
                const selected = answers[String(q.qid)];
                return (
                  <div key={q.qid} className="sacred-card" data-testid={`kosha-q-${q.qid}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-divine-gold/40 text-sm font-heading shrink-0 mt-0.5">
                        Q{(activeKoshaIdx * 5) + idx + 1}
                      </span>
                      <p className="text-chakra-crown text-sm leading-relaxed">{q.text}</p>
                    </div>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => {
                        const isSelected = selected === opt.value;
                        return (
                          <button key={oi} onClick={() => handleAnswer(q.qid, opt.value)}
                            className={`w-full text-left p-3 rounded-lg border transition-all ${
                              isSelected
                                ? `${koshaColors.bg} ${koshaColors.border} ${koshaColors.text}`
                                : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/70 hover:border-divine-gold/30"
                            }`}
                            data-testid={`kosha-q-${q.qid}-opt-${oi}`}
                          >
                            <div className="flex items-center gap-3">
                              {isSelected
                                ? <CheckCircle2 className={`w-4 h-4 ${koshaColors.text} shrink-0`} />
                                : <Circle className="w-4 h-4 text-chakra-crown/30 shrink-0" />}
                              <span className="text-sm">{opt.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-2">
              <div>
                {activeKoshaIdx > 0 && (
                  <button onClick={() => goToKosha(activeKoshaIdx - 1)}
                    className="flex items-center gap-2 text-chakra-crown/50 hover:text-divine-gold transition-colors text-sm"
                    data-testid="kosha-prev">
                    <ChevronLeft className="w-4 h-4" /> Previous Kosha
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                {activeKoshaIdx < koshas.length - 1 ? (
                  <button onClick={() => goToKosha(activeKoshaIdx + 1)}
                    disabled={currentAnswered === 0}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                    data-testid="kosha-next">
                    <ArrowRight className="w-4 h-4" />
                    Next Kosha
                  </button>
                ) : (
                  <button onClick={handleSave}
                    disabled={saving || totalAnswered < 25}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                    data-testid="kosha-submit">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                    SUBMIT QUIZ
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
